import {
  createContext, useContext, useState, useMemo, useCallback, useEffect, useRef,
} from 'react';
import {
  useGetCartQuery,
  useAddCartItemMutation,
  useRemoveCartItemMutation,
  useClearCartMutation,
  useMergeCartMutation,
} from '../store/cartApi';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const STORAGE_KEY = 'trustlab_cart';

const readLocal = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};
const writeLocal = (items) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* quota */ }
};
const clearLocal = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

const computeTotals = (items) => {
  const subtotal = items.reduce((sum, x) => sum + (x.price || 0) * (x.quantity || 1), 0);
  const savings = items.reduce(
    (sum, x) => sum + (((x.original || x.price) - (x.price || 0)) * (x.quantity || 1)),
    0
  );
  return { subtotal, savings, count: items.length };
};

// Server payload → frontend cart item shape used by all consumers.
const fromServer = (s) => ({
  id: s.item_id,
  type: s.item_type,
  name: s.name,
  price: s.price,
  original: s.original,
  icon: s.icon,
  tat: s.tat,
  paramCount: s.paramCount,
});

// Frontend item → server payload shape.
const toServer = (c) => ({
  item_id: String(c.id),
  item_type: c.type,
  name: c.name,
  price: Number(c.price) || 0,
  original: c.original ?? null,
  icon: c.icon || '🧪',
  tat: c.tat || '',
  paramCount: c.paramCount || 0,
});

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [localItems, setLocalItems] = useState(() => readLocal());

  // Server cart only fetched when signed in
  const { data: serverData } = useGetCartQuery(undefined, { skip: !isAuthenticated });
  const [addCartItem] = useAddCartItemMutation();
  const [removeCartItem] = useRemoveCartItemMutation();
  const [serverClearCart] = useClearCartMutation();
  const [mergeCart] = useMergeCartMutation();

  // Persist guest cart to localStorage on every change.
  useEffect(() => {
    if (!isAuthenticated) writeLocal(localItems);
  }, [localItems, isAuthenticated]);

  // On login: push any local items into the server cart, then clear local.
  // mergedRef stops it from re-running on every server-cart refetch.
  const mergedRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      if (mergedRef.current) return;
      mergedRef.current = true;
      const local = readLocal();
      if (local.length) {
        mergeCart(local.map(toServer))
          .unwrap()
          .catch(() => { /* leave local as-is on failure so user can retry */ })
          .finally(() => {
            clearLocal();
            setLocalItems([]);
          });
      }
    } else {
      // Logged out: reset the merge guard and drop any leftover local cart.
      mergedRef.current = false;
      clearLocal();
      setLocalItems([]);
    }
  }, [isAuthenticated, mergeCart]);

  // Single source of truth for the rest of the app.
  const items = useMemo(
    () => (isAuthenticated ? (serverData?.items || []).map(fromServer) : localItems),
    [isAuthenticated, serverData, localItems]
  );

  const addItem = useCallback(
    (item) => {
      if (isAuthenticated) {
        addCartItem(toServer(item));
      } else {
        setLocalItems((prev) => {
          if (prev.some((x) => x.id === item.id && x.type === item.type)) return prev;
          return [...prev, item];
        });
      }
    },
    [isAuthenticated, addCartItem]
  );

  const removeItem = useCallback(
    (id, type) => {
      if (isAuthenticated) {
        removeCartItem({ item_id: id, item_type: type });
      } else {
        setLocalItems((prev) => prev.filter((x) => !(x.id === id && x.type === type)));
      }
    },
    [isAuthenticated, removeCartItem]
  );

  const clearCart = useCallback(() => {
    if (isAuthenticated) {
      serverClearCart();
    } else {
      setLocalItems([]);
    }
  }, [isAuthenticated, serverClearCart]);

  const inCart = useCallback(
    (id, type) => items.some((x) => String(x.id) === String(id) && x.type === type),
    [items]
  );

  const toggleItem = useCallback(
    (item) => {
      if (inCart(item.id, item.type)) removeItem(item.id, item.type);
      else addItem(item);
    },
    [inCart, addItem, removeItem]
  );

  const totals = useMemo(() => {
    if (isAuthenticated && serverData?.totals) return serverData.totals;
    return computeTotals(items);
  }, [items, isAuthenticated, serverData]);

  const value = useMemo(
    () => ({ items, addItem, removeItem, clearCart, toggleItem, inCart, totals }),
    [items, addItem, removeItem, clearCart, toggleItem, inCart, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
